import api from '@/services/api';
import type {
    ThirdPartyAddOn,
    ThirdPartyAgreement,
    ThirdPartyInvoiceableDealer,
    ThirdPartyInvoiceRequest,
    ThirdPartyInvoicingContents,
    ThirdPartyService,
} from '../types/thirdPartyAgreement.types';

interface VehiclePayload {
    make: string | null;
    model: string | null;
    registration: string | null;
    vin: string | null;
}

interface ThirdPartyAgreementPayload {
    reference: string;
    status: string | null;
    start_date: string | null;
    balance_gross_minor: number | null;
    vehicle: VehiclePayload;
}

interface InvoiceableDealerPayload {
    encrypted_id: string;
    public_name: string;
}

interface InclusionPayload {
    description: string;
    cost_gross_minor: number;
}

interface ServicePayload {
    service_number: number;
    name: string;
    due_date: string | null;
    due_mileage: number | null;
    amount_gross_minor: number;
    inclusions: InclusionPayload[];
    content_id: number | null;
    content_type_id: number | null;
}

interface AddOnPayload {
    name: string;
    due_date: string | null;
    amount_gross_minor: number;
    content_id: number | null;
}

interface InvoicingPayload {
    services: ServicePayload[];
    add_ons: AddOnPayload[];
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const isStringOrNull = (value: unknown): value is string | null => value === null || typeof value === 'string';

const isIsoDay = (value: string): boolean => {
    const day = new Date(`${value}T00:00:00Z`);
    return /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(day.getTime()) && day.toISOString().startsWith(value);
};

const isIsoDayOrNull = (value: unknown): value is string | null =>
    value === null || (typeof value === 'string' && isIsoDay(value));

const isVehiclePayload = (value: unknown): value is VehiclePayload =>
    isRecord(value) &&
    isStringOrNull(value.make) &&
    isStringOrNull(value.model) &&
    isStringOrNull(value.registration) &&
    isStringOrNull(value.vin);

const isThirdPartyAgreementPayload = (value: unknown): value is ThirdPartyAgreementPayload =>
    isRecord(value) &&
    typeof value.reference === 'string' &&
    value.reference.trim() !== '' &&
    isStringOrNull(value.status) &&
    isIsoDayOrNull(value.start_date) &&
    (value.balance_gross_minor === null || Number.isInteger(value.balance_gross_minor)) &&
    isVehiclePayload(value.vehicle);

export async function fetchThirdPartyAgreement(vrm: string, reference: string): Promise<ThirdPartyAgreement> {
    const response = await api.get('/dashboard/third-party-agreement', { params: { vrm, reference } });
    const payload: unknown = response.data?.data;

    if (!isThirdPartyAgreementPayload(payload)) {
        throw new Error('Unexpected third-party agreement response.');
    }

    return {
        reference: payload.reference,
        status: payload.status,
        startDate: payload.start_date,
        balanceGrossMinor: payload.balance_gross_minor,
        vehicle: { ...payload.vehicle },
    };
}

const isNonBlank = (value: unknown): value is string => typeof value === 'string' && value.trim() !== '';

const isIntegerOrNull = (value: unknown): value is number | null => value === null || Number.isInteger(value);

const isInclusionPayload = (value: unknown): value is InclusionPayload =>
    isRecord(value) && isNonBlank(value.description) && Number.isInteger(value.cost_gross_minor);

const isPositiveInteger = (value: unknown): value is number =>
    typeof value === 'number' && Number.isInteger(value) && value >= 1;

const isServicePayload = (value: unknown): value is ServicePayload =>
    isRecord(value) &&
    isPositiveInteger(value.service_number) &&
    isNonBlank(value.name) &&
    isIsoDayOrNull(value.due_date) &&
    isIntegerOrNull(value.due_mileage) &&
    Number.isInteger(value.amount_gross_minor) &&
    Array.isArray(value.inclusions) &&
    value.inclusions.every(isInclusionPayload) &&
    isIntegerOrNull(value.content_id) &&
    isIntegerOrNull(value.content_type_id);

const isAddOnPayload = (value: unknown): value is AddOnPayload =>
    isRecord(value) &&
    isNonBlank(value.name) &&
    isIsoDayOrNull(value.due_date) &&
    Number.isInteger(value.amount_gross_minor) &&
    isIntegerOrNull(value.content_id);

const hasUniqueServiceNumbers = (services: ServicePayload[]): boolean =>
    new Set(services.map((service) => service.service_number)).size === services.length;

const isThirdPartyInvoicingPayload = (value: unknown): value is InvoicingPayload =>
    isRecord(value) &&
    Array.isArray(value.services) &&
    value.services.every(isServicePayload) &&
    hasUniqueServiceNumbers(value.services) &&
    Array.isArray(value.add_ons) &&
    value.add_ons.every(isAddOnPayload);

const toService = (service: ServicePayload): ThirdPartyService => ({
    serviceNumber: service.service_number,
    name: service.name,
    dueDate: service.due_date,
    dueMileage: service.due_mileage,
    amountGrossMinor: service.amount_gross_minor,
    inclusions: service.inclusions.map((inclusion) => ({
        description: inclusion.description,
        costGrossMinor: inclusion.cost_gross_minor,
    })),
    contentId: service.content_id,
    contentTypeId: service.content_type_id,
});

const toAddOn = (addOn: AddOnPayload): ThirdPartyAddOn => ({
    name: addOn.name,
    dueDate: addOn.due_date,
    amountGrossMinor: addOn.amount_gross_minor,
    contentId: addOn.content_id,
});

export async function fetchThirdPartyInvoicing(vrm: string, reference: string): Promise<ThirdPartyInvoicingContents> {
    const response = await api.get('/dashboard/third-party-agreement/invoicing', { params: { vrm, reference } });
    const payload: unknown = response.data?.data;

    if (!isThirdPartyInvoicingPayload(payload)) {
        throw new Error('Unexpected third-party invoicing response.');
    }

    return { services: payload.services.map(toService), addOns: payload.add_ons.map(toAddOn) };
}

const isInvoiceableDealerPayload = (value: unknown): value is InvoiceableDealerPayload =>
    isRecord(value) && isNonBlank(value.encrypted_id) && isNonBlank(value.public_name);

const hasUniqueEncryptedIds = (dealers: InvoiceableDealerPayload[]): boolean =>
    new Set(dealers.map((dealer) => dealer.encrypted_id)).size === dealers.length;

const isInvoiceableDealersPayload = (value: unknown): value is { dealers: InvoiceableDealerPayload[] } =>
    isRecord(value) &&
    Array.isArray(value.dealers) &&
    value.dealers.every(isInvoiceableDealerPayload) &&
    hasUniqueEncryptedIds(value.dealers);

export async function fetchThirdPartyInvoiceableDealers(
    vrm: string,
    reference: string,
): Promise<ThirdPartyInvoiceableDealer[]> {
    const response = await api.get('/dashboard/third-party-agreement/invoicing/dealers', {
        params: { vrm, reference },
    });
    const payload: unknown = response.data?.data;

    if (!isInvoiceableDealersPayload(payload)) {
        throw new Error('Unexpected third-party invoiceable dealers response.');
    }

    return payload.dealers.map((dealer) => ({ encryptedId: dealer.encrypted_id, publicName: dealer.public_name }));
}

export async function submitThirdPartyInvoice(
    vrm: string,
    reference: string,
    invoice: ThirdPartyInvoiceRequest,
): Promise<void> {
    const response = await api.post('/dashboard/third-party-agreement/invoicing/invoices', {
        vrm,
        reference,
        content_id: invoice.contentId,
        encrypted_dealer_id: invoice.encryptedDealerId,
        invoice_number: invoice.invoiceNumber,
        mileage: invoice.mileage,
    });

    if (response.data?.data?.submitted !== true) {
        throw new Error('Unexpected third-party invoice response.');
    }
}
